import applePay from '../images/apple-pay.svg';
import bancontactSmall from '../images/bancontact-small.svg';
import bancontact from '../images/bancontact.svg';
import bizum from '../images/bizum.svg';
import blik from '../images/blik.svg';
import clickToPay from '../images/click-to-pay.svg';
import eps from '../images/eps.svg';
import giropay from '../images/giropay.svg';
import googlePay from '../images/google-pay.svg';
import ideal from '../images/ideal.svg';
import klarna from '../images/klarna-badge.svg';
import mbway from '../images/mbway.svg';
import multibancoSmall from '../images/multibanco-small.svg';
import multibanco from '../images/multibanco.svg';
import paypal from '../images/paypal.svg';
import sepa from '../images/sepa.svg';
import sofort from '../images/sofort.svg';
import trustly from '../images/trustly.svg';

import {
  AccountInvoiceStatus,
  AccountStatus,
  Acquirer,
  BillingCategoryKlarna,
  BillingPlans,
  BlacklistItemType,
  CancellationReason,
  CardBrand,
  CardType,
  ChurnReason,
  DocumentStatus,
  DocumentType,
  IdDocumentType,
  LegalForm,
  PaymentMethods,
  Processor,
  RefundReason,
  StatusReason,
  SubscriptionStatus,
  ThreeDSecureFlow,
  ThreeDsStatus,
  TokenizationMethods,
  VerificationMethod
} from 'api/generated';
import applePaySmall from 'images/apple-pay-small.svg';
import googlePaySmall from 'images/google-pay-small.svg';

type IconStyle = {src: string; style: {height: number}};

export const blacklistItemTypeLabels: Record<BlacklistItemType, string> = {
  [BlacklistItemType.CARD]: 'Card',
  [BlacklistItemType.CARD_FINGERPRINT]: 'Card fingerprint',
  [BlacklistItemType.COUNTRY]: 'Country',
  [BlacklistItemType.EMAIL]: 'Email',
  [BlacklistItemType.IP]: 'IP',
  [BlacklistItemType.PHONE]: 'Phone',
  [BlacklistItemType.SESSION_FINGERPRINT]: 'Session fingerprint'
};

export const legalFormLabels: Record<LegalForm, string> = {
  [LegalForm.COMPANY]: 'Company',
  [LegalForm.INDIVIDUAL]: 'Individual',
  [LegalForm.FREELANCE]: 'Freelance'
};

export const cardBrandLabels: Record<CardBrand, string> = {
  [CardBrand.VISA]: 'Visa',
  [CardBrand.AMEX]: 'American Express',
  [CardBrand.DINERS]: 'Diners Club',
  [CardBrand.JCB]: 'JCB',
  [CardBrand.MASTERCARD]: 'Mastercard',
  [CardBrand.UNIONPAY]: 'UnionPay',
  [CardBrand.DISCOVER]: 'Discover',
  [CardBrand.UNKNOWN]: 'Unknown'
};

export const paymentMethodLabels: {[key: string]: string} = {
  [PaymentMethods.BIZUM]: 'Bizum',
  [PaymentMethods.CARD]: 'Card',
  [PaymentMethods.PAYPAL]: 'PayPal',
  [PaymentMethods.BANCONTACT]: 'Bancontact',
  [PaymentMethods.IDEAL]: 'iDEAL',
  [PaymentMethods.SOFORT]: 'Sofort',
  [PaymentMethods.GIROPAY]: 'Giropay',
  [PaymentMethods.KLARNA]: 'Klarna',
  [PaymentMethods.SEPA]: 'SEPA',
  [PaymentMethods.MBWAY]: 'MBWay',
  [PaymentMethods.MULTIBANCO]: 'Multibanco',
  [PaymentMethods.TRUSTLY]: 'Trustly',
  [PaymentMethods.EPS]: 'EPS',
  [PaymentMethods.BLIK]: 'BLIK',
  [PaymentMethods.CARDPRESENT]: 'Card present',
  [PaymentMethods.ALIPAY]: 'Alipay',
  [PaymentMethods.SRTP]: 'SRTP',
  [PaymentMethods.APPLEPAY]: 'Apple Pay',
  [PaymentMethods.GOOGLEPAY]: 'Google Pay',
  [PaymentMethods.CLICKTOPAY]: 'Click to Pay'
};

export const paymentProcessorLabels: Partial<Record<Processor, string>> = {
  [Processor.ADYEN]: 'Adyen',
  [Processor.MONEIX_G]: 'MONEI X Redsys (Comercia)',
  [Processor.MONEIX_F]: 'MONEI X Shift4 (Finaro)',
  [Processor.MONEIX_P]: 'PPRO',
  [Processor.MONEI_REDSYS]: 'MONEI Redsys',
  [Processor.MONEIX_GNET]: 'MONEI X Redsys (Getnet)',
  [Processor.PAYPAL]: 'PayPal',
  [Processor.REDSYS]: 'Redsys (Custom)',
  [Processor.STRIPE]: 'Stripe'
};

export const shortProcessorLabels: Partial<Record<Processor, string>> = {
  [Processor.ADYEN]: 'Adyen',
  [Processor.MONEIX_G]: 'Redsys (Comercia)',
  [Processor.MONEIX_F]: 'Shift4 (Finaro)',
  [Processor.MONEIX_P]: 'PPRO',
  [Processor.MONEI_REDSYS]: 'Redsys',
  [Processor.MONEIX_GNET]: 'Redsys (Getnet)',
  [Processor.PAYPAL]: 'PayPal',
  [Processor.REDSYS]: 'Redsys (Custom)',
  [Processor.STRIPE]: 'Stripe'
};

export const getProcessorLabel = (
  processor: Processor,
  acquirer?: Acquirer | null,
  short = false
): string | undefined => {
  const labels = short ? shortProcessorLabels : paymentProcessorLabels;
  const label = labels[processor];
  if (processor === Processor.MONEI_REDSYS && acquirer && label) {
    return `${label} (${acquirerLabels[acquirer]})`;
  }
  return label;
};

export const tokenizationMethodLabels: Record<TokenizationMethods, string> = {
  [TokenizationMethods.GOOGLEPAY]: 'Google Pay',
  [TokenizationMethods.APPLEPAY]: 'Apple Pay',
  [TokenizationMethods.CLICKTOPAY]: 'Click to Pay'
};

export const cardTypeLabels: Record<CardType, string> = {
  [CardType.CREDIT]: 'Credit',
  [CardType.DEBIT]: 'Debit'
};

export const documentLabels: {[key: string]: string} = {
  contract: 'ANEXO I Contract',
  docIdentityFront: 'National identity card (DNI) / passport of business owner (Front)',
  docIdentityBack: 'National identity card (DNI) / passport of business owner (Back)',
  docCertificationAccountOwnership: 'Certification of bank account ownership',
  docBankReceipt: 'Bank receipt',
  docFreelancerReceipt: 'Freelancer receipt or form 36/37',
  docIdentityAdministratorFront:
    'National identity card (DNI) / passport of representative (Front)',
  docIdentityAdministratorBack: 'National identity card (DNI) / passport of representative (Back)',
  docPowerAttorney: 'Power of attorney',
  docArticlesOfIncorporation: 'Articles of Incorporation and other pertinent deeds',
  docCifTaxIdNumber: 'Tax Identification Number (CIF)',
  docValidityDeclaration: 'Responsible declaration of validity of the documentation',
  docTaxUpToDate: 'Certification of being up to date with tax obligations',
  docSignedContract: 'Contract (Signed)',
  docSepa: 'SEPA document',
  docTaxIdCard: 'Tax identification card',
  docKyc: 'Customer knowledge form (Signed)'
};

export const acquirerLabels: Record<Acquirer, string> = {
  [Acquirer.MONEI_X]: 'MONEI X',
  [Acquirer.BANKIA]: 'Bankia',
  [Acquirer.KUTXABANK]: 'Kutxabank',
  [Acquirer.SANTANDER]: 'Santander',
  [Acquirer.STRIPE]: 'Stripe',
  [Acquirer.UNICAJA]: 'Unicaja',
  [Acquirer.PAYCOMET]: 'PAYCOMET',
  [Acquirer.SABADELL]: 'Banco Sabadell',
  [Acquirer.PAYPAL]: 'PayPal',
  [Acquirer.BBVA]: 'BBVA',
  [Acquirer.COMERCIAGLOBALPAY]: 'Comercia Global Payments',
  [Acquirer.CREDITANDORRA]: 'Credit Andorra',
  [Acquirer.BANKINTER]: 'Bankinter',
  [Acquirer.ADYEN]: 'Adyen',
  [Acquirer.CAJASUR]: 'CajaSur',
  [Acquirer.BANCAMARCH]: 'Banca March',
  [Acquirer.CAJAMAR]: 'Cajamar',
  [Acquirer.CAJARURAL]: 'Caja Rural',
  [Acquirer.CAJALABORAL]: 'Caja Laboral',
  [Acquirer.ABANCA]: 'Abanca',
  [Acquirer.DEUTSCHE_BANK]: 'Deutsche Bank',
  [Acquirer.COFIDIS]: 'Cofidis',
  [Acquirer.CUSTOM]: "Merchant's acquirer",
  [Acquirer.GETNET]: 'Getnet'
};

export const paymentMethodIcons: Partial<Record<PaymentMethods, IconStyle>> = {
  [PaymentMethods.BIZUM]: {src: bizum, style: {height: 18}},
  [PaymentMethods.PAYPAL]: {src: paypal, style: {height: 18}},
  [PaymentMethods.MULTIBANCO]: {src: multibanco, style: {height: 22}},
  [PaymentMethods.KLARNA]: {src: klarna, style: {height: 22}},
  [PaymentMethods.IDEAL]: {src: ideal, style: {height: 32}},
  [PaymentMethods.GIROPAY]: {src: giropay, style: {height: 26}},
  [PaymentMethods.MBWAY]: {src: mbway, style: {height: 22}},
  [PaymentMethods.SOFORT]: {src: sofort, style: {height: 14}},
  [PaymentMethods.SEPA]: {src: sepa, style: {height: 16}},
  [PaymentMethods.TRUSTLY]: {src: trustly, style: {height: 16}},
  [PaymentMethods.BANCONTACT]: {src: bancontact, style: {height: 14}},
  [PaymentMethods.EPS]: {src: eps, style: {height: 32}},
  [PaymentMethods.BLIK]: {src: blik, style: {height: 24}}
};

export const tokenizationMethodIcons: Record<TokenizationMethods, IconStyle> = {
  [TokenizationMethods.GOOGLEPAY]: {src: googlePay, style: {height: 18}},
  [TokenizationMethods.APPLEPAY]: {src: applePay, style: {height: 18}},
  [TokenizationMethods.CLICKTOPAY]: {src: clickToPay, style: {height: 16}}
};

export const paymentMethodMinimalIcons: Partial<Record<PaymentMethods, IconStyle>> = {
  [PaymentMethods.BIZUM]: {src: bizum, style: {height: 18}},
  [PaymentMethods.PAYPAL]: {src: paypal, style: {height: 18}},
  [PaymentMethods.MULTIBANCO]: {src: multibancoSmall, style: {height: 22}},
  [PaymentMethods.KLARNA]: {src: klarna, style: {height: 22}},
  [PaymentMethods.IDEAL]: {src: ideal, style: {height: 21}},
  [PaymentMethods.GIROPAY]: {src: giropay, style: {height: 20}},
  [PaymentMethods.MBWAY]: {src: mbway, style: {height: 19}},
  [PaymentMethods.SOFORT]: {src: sofort, style: {height: 20}},
  [PaymentMethods.SEPA]: {src: sepa, style: {height: 14}},
  [PaymentMethods.TRUSTLY]: {src: trustly, style: {height: 16}},
  [PaymentMethods.BANCONTACT]: {src: bancontactSmall, style: {height: 22}},
  [PaymentMethods.EPS]: {src: eps, style: {height: 22}},
  [PaymentMethods.BLIK]: {src: blik, style: {height: 19}}
};
export const tokenizationMethodMinimalIcons: Record<TokenizationMethods, IconStyle> = {
  [TokenizationMethods.GOOGLEPAY]: {src: googlePaySmall, style: {height: 18}},
  [TokenizationMethods.APPLEPAY]: {src: applePaySmall, style: {height: 18}},
  [TokenizationMethods.CLICKTOPAY]: {src: clickToPay, style: {height: 14}}
};

export const refundReasonLabels: Record<RefundReason, string> = {
  [RefundReason.DUPLICATED]: 'Duplicated',
  [RefundReason.FRAUDULENT]: 'Fraudulent',
  [RefundReason.REQUESTED_BY_CUSTOMER]: 'Requested by customer',
  [RefundReason.ORDER_CANCELED]: 'Order canceled'
};

export const cancellationReasonLabels: Record<CancellationReason, string> = {
  [CancellationReason.DUPLICATED]: 'Duplicated',
  [CancellationReason.FRAUDULENT]: 'Fraudulent',
  [CancellationReason.REQUESTED_BY_CUSTOMER]: 'Requested by customer',
  [CancellationReason.ORDER_CANCELED]: 'Order canceled'
};

export const idDocumentTypeLabels: Record<string, string> = {
  [IdDocumentType.ID_CARD]: 'ID Card',
  [IdDocumentType.RESIDENCE_PERMIT]: 'Residence Permit',
  [IdDocumentType.PASSPORT]: 'Passport',
  [IdDocumentType.DRIVERS_LICENSE]: 'Drivers License'
};

export const accountStatusLabels: Record<AccountStatus, string> = {
  [AccountStatus.ACTIVE]: 'Active',
  [AccountStatus.APPROVED]: 'Approved',
  [AccountStatus.SUSPENDED]: 'Suspended',
  [AccountStatus.REJECTED]: 'Rejected',
  [AccountStatus.DELETED]: 'Deleted',
  [AccountStatus.NOT_CONFIGURED]: 'Pending additional data',
  [AccountStatus.PENDING_CONTRACT]: 'Pending KYC',
  [AccountStatus.PENDING_APPROVAL]: 'Pending approval',
  [AccountStatus.PENDING_REVIEW]: 'Pending review'
};

export const billingPlans: Record<BillingPlans, string> = {
  [BillingPlans.MONEI_PLUS]: 'MONEI PLUS',
  [BillingPlans.MONEI_POS]: 'MONEI POS',
  [BillingPlans.MONEI_X]: 'MONEI X'
};

export const documentTypeLabels: Record<DocumentType, string> = {
  [DocumentType.CENSUS_CERT]: 'Certificate of census situation',
  [DocumentType.GIPUZKOA_CENSUS_CERT]: 'Certificate of census situation (Gipuzkoa)',
  [DocumentType.NAVARRA_CENSUS_CERT]: 'Certificate of census situation (Navarra)',
  [DocumentType.BIZKAIA_CENSUS_CERT]: 'Certificate of census situation (Bizkaia)',
  [DocumentType.ALAVA_CENSUS_CERT]: 'Certificate of census situation (Álava)',
  [DocumentType.BANK_OWNERSHIP_CERT]: 'Certificate of ownership of the bank account',
  [DocumentType.COMPANY_TAX_ID]: 'Copy of CIF',
  [DocumentType.BENEFICIAL_OWNERSHIP_REPORT]: 'Beneficial ownership report',
  [DocumentType.DEED_OF_COMPANY_REPRESENTATIVE]:
    'Deed containing the appointment of the legal representative',
  [DocumentType.DEED_OF_INCORPORATION]: 'Deed of incorporation of the company',
  [DocumentType.CONTRACT]: 'Contract',
  [DocumentType.DRIVERS_LICENSE]: 'Driver’s license',
  [DocumentType.ID_CARD]: 'ID card',
  [DocumentType.PASSPORT]: 'Passport',
  [DocumentType.PIBISI_REPORT]: 'PIBISI report',
  [DocumentType.POWER_OF_ATTORNEY]: 'Power of attorney',
  [DocumentType.RESIDENCE_PERMIT]: 'Residence permit',
  [DocumentType.MERCANTILE_REGISTRY_NOTE]: 'Nota informativa de Registro Mercantil',
  [DocumentType.VERIFF_REPORT]: 'Veriff report',
  [DocumentType.OTHER]: 'Other'
};

export const documentStatusLabels: Record<DocumentStatus, string> = {
  [DocumentStatus.APPROVAL_REQUIRED]: 'Approval required',
  [DocumentStatus.APPROVED]: 'Approved',
  [DocumentStatus.EXPIRED]: 'Expired',
  [DocumentStatus.REJECTED]: 'Rejected',
  [DocumentStatus.PENDING]: 'Pending',
  [DocumentStatus.PROCESSING]: 'Processing'
};

export const documentCodes: Record<string, string> = {
  D000: 'Document is approved',
  D001: 'Document requires manual review',
  D002: 'Invalid document format',
  D003: 'Document does not contain required information',
  D004: 'Document should be issued in the last 3 months',
  D005: 'Document is issued to another legal entity',
  D999: 'Service internal error'
};

export const documentErrorCodes: Record<string, string> = {
  D002: 'Invalid document format',
  D003: 'Document does not contain required information',
  D004: 'Document should be issued in the last 3 months',
  D005: 'Document is issued to another legal entity'
};

export const churnReasonLabels: Record<ChurnReason, string> = {
  [ChurnReason.ECONOMIC_CONDITIONS]: 'Economic conditions',
  [ChurnReason.LACK_SUPPORT]: 'Lack of support from MONEI',
  [ChurnReason.PROBLEM_WITH_PLATFORM]: 'Problem with the platform',
  [ChurnReason.PROBLEM_WITH_WEBSITE]: 'Problem with the website',
  [ChurnReason.SALES_DROP]: 'Sales drop',
  [ChurnReason.SWITCHED]: 'Switched to another payment provider',
  [ChurnReason.CLOSED_BUSINESS]: 'Closed business',
  [ChurnReason.OTHER]: 'Other'
};

export const statusReasonLabels: Record<StatusReason, string> = {
  [StatusReason.CHARGEBACKS]: 'Too many chargebacks',
  [StatusReason.CUSTOMER_COMPLAINTS]: 'Too many customer complaints',
  [StatusReason.CUSTOMER_REQUEST]: 'Customer request',
  [StatusReason.DOMAIN_MISMATCH]: 'Website domain mismatch',
  [StatusReason.SUSPICIOUS_ACTIVITY]: 'Suspicious activity',
  [StatusReason.UPDATE_BILLING_INFO]: 'Update billing information',
  [StatusReason.INCOMPLETE_DOCS]: 'Incomplete documentation',
  [StatusReason.RESTRICTED_CATEGORY]: 'Restricted merchant category',
  [StatusReason.NOT_COMPLIANT_BUSINESS]: 'Not compliant business',
  [StatusReason.REAL_HOLDERS_DOCS]: 'Beneficial owners documents',
  [StatusReason.NON_COMMERCIAL_COMPANY_DOCS]: 'Non-commercial company',
  [StatusReason.NON_COMPLIANT_WEBSITE]: 'Website is not compliant',
  [StatusReason.OTHER_COMPANY_WEBSITE]: 'Website does not correspond to the account holder',
  [StatusReason.HIGH_RISK_GOODS]: 'High risk goods or services',
  [StatusReason.JOINT_OWNERSHIP]: 'Joint ownership',
  [StatusReason.REPEAT_ONBOARDING]: 'Incomplete onboarding (should be repeated)',
  [StatusReason.CBD_PRODUCTS]: 'CBD products',
  [StatusReason.OTHER]: 'Other reason (specify in the note)',
  [StatusReason.BANK_OWNERSHIP_CERT_REQUIRED]: 'Bank ownership certificate required',
  [StatusReason.ID_VERIFICATION_REQUIRED]: 'ID verification required',
  [StatusReason.CENSUS_CERT_REQUIRED]: 'Census certificate required'
};

export const accountInvoiceStatusLabels: Record<AccountInvoiceStatus, string> = {
  [AccountInvoiceStatus.PAID]: 'Paid',
  [AccountInvoiceStatus.PENDING]: 'Pending',
  [AccountInvoiceStatus.PAST_DUE]: 'Past due',
  [AccountInvoiceStatus.UNPAID]: 'Unpaid'
};

export const switchedChurnReasonOptions = [
  'Stripe',
  'Adyen',
  'Redsys',
  'PAYCOMET',
  'Shopify Payments',
  'Square',
  'PayPal',
  'Chargebee',
  '2Checkout',
  'ACI Worldwide',
  'Klarna',
  'Mollie',
  'Other'
];

export const platformChurnReasonOptions = [
  'Shopify',
  'WooCommerce',
  'Magento',
  'Salesforce',
  'PrestaShop',
  'Wix',
  'Other'
];

export const subscriptionStatusLabels: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.ACTIVE]: 'Active',
  [SubscriptionStatus.CANCELED]: 'Cancelled',
  [SubscriptionStatus.EXPIRED]: 'Expired',
  [SubscriptionStatus.PENDING]: 'Pending',
  [SubscriptionStatus.PAUSED]: 'Paused',
  [SubscriptionStatus.PAST_DUE]: 'Past due',
  [SubscriptionStatus.TRIALING]: 'In trial'
};

export const threeDSecureFlowStatus: Record<ThreeDSecureFlow, string> = {
  [ThreeDSecureFlow.FRICTIONLESS_CHALLENGE]: 'Frictionless and challenge',
  [ThreeDSecureFlow.CHALLENGE]: 'Challenge',
  [ThreeDSecureFlow.DIRECT]: 'Direct',
  [ThreeDSecureFlow.FRICTIONLESS]: 'Frictionless'
};

export const verificationMethodLabels: Record<VerificationMethod, string> = {
  [VerificationMethod.ONLINEPIN]: 'Online PIN',
  [VerificationMethod.OFFLINEPIN]: 'Offline PIN'
};

export const threeDSecureStatus: Record<ThreeDsStatus, string> = {
  [ThreeDsStatus.A]: 'Attempts Processing Performed',
  [ThreeDsStatus.I]: 'Informational Only',
  [ThreeDsStatus.N]: 'Transaction Denied',
  [ThreeDsStatus.R]: 'Account Verification Rejected',
  [ThreeDsStatus.U]: 'Account Verification Could Not Be Performed',
  [ThreeDsStatus.Y]: 'Authentication Successful'
};

export const klarnaBillingCategory: Record<BillingCategoryKlarna, string> = {
  [BillingCategoryKlarna.PAY_LATER]: 'Pay Later',
  [BillingCategoryKlarna.SLICE_IT]: 'Fair Financing',
  [BillingCategoryKlarna.PAY_NOW]: 'Pay Now',
  [BillingCategoryKlarna.SLICE_IT_BY_CARD]: 'Pay in 3',
  [BillingCategoryKlarna.PAY_IN_PARTS]: 'Pay in Parts'
};
